/* ============================================================
   畫質設定 —— 三段，存 localStorage
   ============================================================

   使用者 2026-08-04（質感這一輪）在選項裡挑了「做正式畫質設定選單」，
   而不是「自動偵測降級」。理由是這一輪加的四樣東西
   （泛光、2048² 陰影、地面的法線／粗糙度／環境反射、建築的法線圖）
   都是**累加的 fragment 與 VRAM 成本**，而使用者最清楚自己的機器。

   ## 這個檔為什麼不 import 任何渲染模組

   套用畫質要動 `render/postfx`、`render/scene`、`world/arena`、
   `world/maps/ruin-kit` —— 而那些模組都要讀畫質旗標來決定「要不要建」。
   兩邊互相 import 就是循環。所以這裡只有**狀態與訂閱**，
   由各模組自己註冊 `onQuality()`，誰負責的東西誰套用。

   ## 「低」為什麼不是「把東西關掉」而已

   最貴的一項其實在**建立**不在每幀：地面的法線圖是 1024² 的 Sobel。
   所以 `floorDetail` 為 false 時 `arena.ts` **根本不會算**，
   不是算完再關掉 —— 低階機器連那 100 ms 的開場卡頓都不會有。 */

export type QLevel = 'high' | 'medium' | 'low';

export type QFlags = {
  level: QLevel;
  /** 泛光（全螢幕 pass）。 */
  bloom: boolean;
  /** 陰影圖邊長。 */
  shadowMap: number;
  /** 地面的法線＋粗糙度貼圖（建立時要跑 1024² Sobel）。 */
  floorDetail: boolean;
  /** 地面的環境反射（濕地）。吃 fragment，而且要多一張 PMREM。 */
  floorEnv: boolean;
  /** 建築材質的法線圖（八張 Sobel，各 128²–512²）。 */
  buildingNormals: boolean;
};

var PRESETS: Record<QLevel, QFlags> = {
  high: { level: 'high', bloom: true, shadowMap: 2048, floorDetail: true, floorEnv: true, buildingNormals: true },
  /* 中：保留「有沒有光」與「表面有沒有起伏」這兩件讀得出來的事，
     砍掉最貴而最不明顯的一項（濕地反射在俯視鏡頭下本來就只是微光）。 */
  medium: { level: 'medium', bloom: true, shadowMap: 1024, floorDetail: true, floorEnv: false, buildingNormals: true },
  /* 低：只留真陰影與接地影。接地影不砍 —— 它是一個 draw call，
     而少了它東西會**看起來浮在地上**，那是比畫質低更嚴重的問題。 */
  low: { level: 'low', bloom: false, shadowMap: 1024, floorDetail: false, floorEnv: false, buildingNormals: false },
};

export var QUALITY: QFlags = PRESETS.high;

var KEY = 'chassis0:quality:v1';
var subs: ((q: QFlags) => void)[] = [];

/** 註冊套用函式。**註冊當下會立刻被呼叫一次** ——
    模組初始化的順序不固定，少了這一次的話先註冊的人會停在預設值。 */
export function onQuality(fn: (q: QFlags) => void) {
  subs.push(fn);
  fn(QUALITY);
}

export function setQuality(level: QLevel) {
  QUALITY = PRESETS[level] || PRESETS.high;
  try { localStorage.setItem(KEY, QUALITY.level); } catch (e) { /* 無痕模式會丟 */ }
  for (var i = 0; i < subs.length; i++) subs[i](QUALITY);
  return QUALITY;
}

/** 開機時讀回上次的選擇。**要盡量早呼叫**（`boot()` 的第一行）：
    「低」的意義是連 Sobel 都不算，晚一步讀就白花那 100 ms。

    ⚠️ **讀完一定要通知已經註冊的訂閱者。** 模組層級註冊的那些
    （`render/scene.ts` 的陰影解析度）在 import 期間就跑完了，那時候
    `QUALITY` 還是預設的「高」—— 少了這一圈通知，症狀是
    「選了低畫質但陰影圖還是 2048」，而且介面顯示的是「低」，
    看起來像設定沒存到，真因卻在通知的時機。 */
export function loadQuality() {
  var v: any = null;
  try { v = localStorage.getItem(KEY); } catch (e) { /* 同上 */ }
  if (v && PRESETS[v as QLevel]) QUALITY = PRESETS[v as QLevel];
  for (var i = 0; i < subs.length; i++) subs[i](QUALITY);
  return QUALITY;
}
